import autobind from "autobind-decorator";
import type { ReactNode } from "react";
import type { LeadRequest } from "@/component/assets/AbstractLeadForm/types";
import type {
	PersonalLoanLeadCalcRequest,
	PersonalLoanLeadCalcResponse,
	PersonalLoanLeadFormFields,
	PersonalLoanLeadFormState,
} from "@/component/PersonalLoan/LeadForm";
import { LeadForm } from "@/component/PersonalLoan/LeadForm";
import type { EventComponentType } from "@/shared/lib/google-analytics-4.type";
import { logError } from "@/shared/lib/sentry-error";
import { calculate, responseToResult } from "../Calculator/domain";
import type { PersonalLoanRequest, PersonalLoanResponseItem } from "../Calculator/types";
import { INITIAL_VALUES } from "./data";
import * as Step from "./Steps";
import "./style.scss";

export interface PersonalLoanSmallCalculatorFormFields extends PersonalLoanLeadFormFields {
	maturity: number;
	monthlyIncome: number;
}

export interface PersonalLoanSmallCalculatorState
	extends PersonalLoanLeadFormState<PersonalLoanSmallCalculatorFormFields> {
	result: PersonalLoanResponseItem;
}

/**
 * @Shortcode
 *
 * @title "Személyi hitel kalkulátor"
 * @shortcode "personal_loans_small"
 * @slug "szemelyi-hitel"
 * @type "calculator"
 */
export class PersonalLoanSmallCalculator extends LeadForm<
	PersonalLoanSmallCalculatorFormFields,
	PersonalLoanSmallCalculatorState
> {
	// EventParams
	protected readonly productTypes: string = "PERSONAL_LOAN";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		outsideTitle: "Személyi hitel kalkulátor",
		outsideTitleLink: "/szemelyi-kolcson/",
		title: "Kérd szakértőnk segítségét!",
		bottomLogo: true,
	};

	protected getInitialState(): PersonalLoanSmallCalculatorState {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			result: undefined,
		} as unknown as PersonalLoanSmallCalculatorState;
	}

	protected renderSteps(): ReactNode {
		const { variant } = this.props;
		const { result, submitState, values, leadId } = this.state;
		const response = this.getCalculatorResponse();

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="result" />
				<Step.Khr stepName="khr" />
				<Step.Error stepName="error" defaultTarget="step-1" canStepBack />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					result={result}
					allValues={values}
					className="personal-loan-result"
					canStepBack
				/>
				<Step.LeadForm
					stepName="lead-form"
					submitState={submitState}
					defaultTarget="success-lead"
					button="Tájékoztatást kérek"
					title="Kérd részletes eredményeidet email-ben és várd szakértőnk hívását, hogy segítsen a felmerülő kérdések tisztázásában!"
					loanAmount={values.loanAmount}
					maturityYears={undefined}
					installment={undefined}
					apr={undefined}
					variant={variant}
					crossSellOption
				/>
				<Step.SuccessPage
					stepName="success-lead"
					leadId={leadId}
					userEmail={values.email}
					ga4SendEventParameters={{
						providerName: response?.bankName,
						productName: response?.productName,
						productId: response?.id,
					}}
				/>
			</>
		);
	}

	private getSearchParams(): PersonalLoanRequest {
		const {
			values: { loanAmount, maturity, monthlyIncome },
		} = this.state;

		return {
			...INITIAL_VALUES,
			loanAmount,
			maturityYears: maturity,
			incomeList: [
				{
					incomeType: "SALARY-H",
					incomeTypeName: "Magyar alkalmazotti jövedelem",
					amount: monthlyIncome,
					receiptAmount: monthlyIncome,
					durationMonths: 12,
					isCash: false,
				},
			],
		};
	}

	protected onStep(target: string, withEvent: boolean): void {
		const {
			values: { inKHR },
		} = this.state;
		switch (target) {
			case "result":
				if (inKHR) {
					super.onStep("khr", withEvent);
				} else {
					this.onResult(target, withEvent);
				}
				break;

			default:
				super.onStep(target, withEvent);
				break;
		}
	}

	@autobind
	private onResult(target: string, withEvent: boolean): void {
		this.setState({
			submitState: "loading",
		});

		const request = this.getSearchParams();
		this.promisePool
			.add(calculate(request))
			.then((response) => {
				const results = responseToResult(request, response, true, "without-ct");

				if (!results?.normalList.length) {
					super.onStep("error", withEvent);

					this.sendCalculationEvent(["Sajnos nem találtunk a megadott feltételeknek megfelelő hitelt."]);
				} else {
					this.setState(
						{
							result: results.normalList[0],
							submitState: "done",
						},
						() => {
							super.onStep(target, withEvent);
						},
					);

					this.sendCalculationEvent();
				}
			})
			.catch((error: Error) => {
				this.setState({
					submitState: "error",
				});

				logError(error);
			})
			.finally(() => {
				this.promisePool.addTimeout().then(() => {
					this.setState({
						submitState: undefined,
					});
				});
			});
	}

	@autobind
	protected getLeadData(): LeadRequest<PersonalLoanLeadCalcRequest, PersonalLoanLeadCalcResponse> {
		const { result, values } = this.state;
		const data = super.getLeadData();
		data.response = { ...data.response, ...result };
		data.request = {
			...data.request,
			maturityYears: values.maturity,
			incomeList: [
				{
					incomeType: "SALARY-H",
					incomeTypeName: "Magyar alkalmazotti jövedelem",
					amount: values.monthlyIncome,
					receiptAmount: values.monthlyIncome,
					isCash: false,
				},
			],
		};

		return data;
	}

	protected getClassName(): string {
		return `${super.getClassName()} personal-loan-small-calculator`;
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.PersonalLoanSmallCalculator`;
	}
}

export default PersonalLoanSmallCalculator;
